/**
 * @file contexts.ts
 * @description Где какие элементы секции разрешены (phase19.16 Р2а «Каталог и
 * товар»; db-design-r2a, ответ 2б; uiux-design-r2a §3).
 *
 * Секция одна на всё (SB22), но элементы данных товара (`product-*`,
 * `breadcrumbs`) имеют смысл только на странице товара: данные берутся из
 * товара, которого на обычной странице нет. Поэтому контекст задаёт, какие
 * виды можно ставить:
 *   page            — обычная страница сайта;
 *   slot            — место для секций вне страниц (блог, каталог);
 *   productTemplate — шаблон страницы товара: общие виды + данные товара.
 * Одна таблица на сервер (проверка записи, 422), кабинет (меню «⊕ элемент» с
 * группами) и чтение (`readSectionPayload` снимает чужие виды). Новый контекст
 * (шаблон категории, статьи — Р3) — новая запись здесь (OCP).
 *
 * Реестр `PRODUCT_ELEMENT_INFO` — подписи элементов данных товара; признак
 * «обязательный» не задаётся руками, а берётся из `PRODUCT_TEMPLATE_REQUIRED`
 * (приёмка Р2а 2026-10-11: «Продавец» стал необязательным правкой константы).
 */

import {
  PRODUCT_ELEMENT_KINDS,
  PRODUCT_TEMPLATE_REQUIRED,
  SECTION_ELEMENT_KINDS_V2,
  type ProductElementKind,
  type SectionElementKind,
} from "./schema.js";

/** Контексты секции. */
export const SECTION_CONTEXTS = ["page", "slot", "productTemplate"] as const;
export type SectionContext = (typeof SECTION_CONTEXTS)[number];

/**
 * Разрешённые виды по контексту. В шаблоне товара данные товара идут первыми —
 * это первая группа меню «⊕ элемент» (uiux-design-r2a §3).
 */
export const SECTION_ELEMENT_CONTEXTS: Readonly<Record<SectionContext, readonly SectionElementKind[]>> = {
  page: SECTION_ELEMENT_KINDS_V2,
  slot: SECTION_ELEMENT_KINDS_V2,
  productTemplate: [...PRODUCT_ELEMENT_KINDS, ...SECTION_ELEMENT_KINDS_V2],
};

/** Можно ли поставить элемент вида `kind` в контексте `context`. */
export function isElementAllowedIn(kind: SectionElementKind, context: SectionContext): boolean {
  return SECTION_ELEMENT_CONTEXTS[context].includes(kind);
}

export interface ProductElementInfo {
  readonly kind: ProductElementKind;
  /** Название в меню, структуре и сообщениях: «Цена и покупка». */
  readonly label: string;
  /** Подсказка в меню «⊕ элемент». */
  readonly hint: string;
  /**
   * SB34: обязательный — нельзя удалить и скрыть, можно переставить.
   * Считается из `PRODUCT_TEMPLATE_REQUIRED` (с 2026-10-11 их три: название,
   * цена и покупка, сведения о товаре; «Продавец» — обычный элемент, SB36).
   */
  readonly required: boolean;
}

const info = (kind: ProductElementKind, label: string, hint: string): ProductElementInfo => ({
  kind,
  label,
  hint,
  required: (PRODUCT_TEMPLATE_REQUIRED as readonly string[]).includes(kind),
});

/** Реестр элементов данных товара (порядок — порядок группы «Данные товара»). */
export const PRODUCT_ELEMENT_INFO: Readonly<Record<ProductElementKind, ProductElementInfo>> = {
  "product-gallery": info("product-gallery", "Галерея", "Фото товара с миниатюрами"),
  "product-title": info("product-title", "Название", "Название товара — главный заголовок страницы (H1)"),
  "product-buy": info("product-buy", "Цена и покупка", "Фасовки, цена, количество и кнопка «В корзину»"),
  "product-badges": info("product-badges", "Бейджи", "Новинка, бестселлер, скидка, «скоро в продаже»"),
  "product-short": info("product-short", "Короткое описание", "Подзаголовок товара в одну-две строки"),
  "product-description": info("product-description", "Полное описание", "Текст описания товара"),
  "product-facts": info("product-facts", "Сведения о товаре", "Состав, сроки, хранение, производитель, декларация"),
  "product-specs": info("product-specs", "Характеристики", "Характеристики товара таблицей или строками"),
  "product-tags": info("product-tags", "Метки", "Метки товара со ссылками в каталог"),
  "product-reviews": info("product-reviews", "Отзывы о товаре", "Отзывы покупателей и «Все отзывы»"),
  "product-related": info("product-related", "Похожие товары", "Товары той же категории"),
  breadcrumbs: info("breadcrumbs", "Хлебные крошки", "Главная › Каталог › категория › товар"),
  "product-seller": info("product-seller", "Продавец", "Реквизиты продавца из профиля юрлица"),
};
